/**
 * What each canvas layer should draw this frame.
 *
 * Pure decisions, no canvas: given what a layer already holds and what the board now
 * has, work out whether the layer can simply be added to or has to be started again.
 * Getting these right is most of what keeps a three-hour lesson feeling new — the
 * alternative is redrawing every stroke ever made, every time the pen lifts.
 */

import type { ViewportState } from '@opal/contracts';
import {
  visibleCanvasRect,
  type Layer,
  type Rect,
  type Stroke,
  type StrokeTool,
  type Viewport,
} from '@opal/shared';

import { layerRank } from './board-layers.js';

import { clearCanvas, drawInk, styleOf } from './ink.js';
import type { Quadtree } from './quadtree.js';
import { toleranceFor } from './simplify.js';
import { simplifiedPointsOf } from './stroke-bounds.js';

/** What the active layer already has drawn on it: how much, and under which view. */
export interface PaintMemory {
  points: number;
  key: string | null;
}

/** What the active layer should draw this frame. */
export interface ActivePaintPlan {
  readonly clear: boolean;
  /** Index of the first sample to draw. */
  readonly from: number;
}

/**
 * Decides whether this frame can add to what is already drawn, or must start over.
 *
 * Adding is the fast path and the reason a ten-second scribble stays smooth: the
 * board draws the two or three samples that just arrived, not the two thousand before
 * them. It is only safe when nothing else changed — a new stroke, a zoom, a pan or a
 * see-through tool all force a clean redraw, the last because overlapping half-opaque
 * ink would darken where it doubled up.
 */
export function planActivePaint(
  painted: PaintMemory,
  key: string,
  canAppend: boolean,
): ActivePaintPlan {
  if (!canAppend || painted.key !== key || painted.points === 0) {
    return { clear: true, from: 0 };
  }
  // Redraw the last piece as well, so the join and the tip line up exactly.
  return { clear: false, from: Math.max(0, painted.points - 1) };
}

/** Everything about the view that changes what the ink looks like on screen. */
export function viewportKeyOf(viewport: Viewport | ViewportState): string {
  return [
    viewport.zoom,
    viewport.panX,
    viewport.panY,
    viewport.widthPx,
    viewport.heightPx,
  ].join(':');
}

/** The same, plus which stroke is in hand, for the layer that draws it. */
export function viewportKey(viewport: ViewportState, strokeSeq: number): string {
  return `${strokeSeq}:${viewportKeyOf(viewport)}`;
}

/**
 * Which band a stroke paints in, lowest first (SRS C4, SRS C8).
 *
 * Two things decide it. Highlighter ink always goes under pen ink, and the
 * teacher-only layer always goes over everything in the lesson — so the answers a
 * teacher writes for themselves sit on top of the working they are written over.
 */
export function paintBand(stroke: { readonly tool: StrokeTool; readonly layer: Layer }): number {
  return layerRank(stroke.layer) * 2 + (stroke.tool === 'highlighter' ? 0 : 1);
}

/** How many bands there are: highlighter and pen, on each of the two work layers. */
export const BAND_COUNT = 4;

/** What the board layer already holds, band by band, and under which view. */
export interface BoardMemory {
  /** One count per band, in band order. */
  counts: number[];
  key: string | null;
}

/** An empty memory: nothing painted yet, in any band. */
export function emptyBoardMemory(): BoardMemory {
  return { counts: Array.from({ length: BAND_COUNT }, () => 0), key: null };
}

/** What the board layer should draw this frame: from where, in each band. */
export interface BoardPaintPlan {
  readonly clear: boolean;
  /** The first stroke to draw in each band. */
  readonly from: readonly number[];
}

/**
 * Whether the board layer can simply add the strokes that have landed since it last
 * painted, or has to be redrawn from scratch.
 *
 * This is the difference between a lesson that stays quick and one that does not.
 * Redrawing every finished stroke each time the pen lifts costs more with every
 * stroke on the board, and the teacher feels it as the next letter arriving late.
 *
 * Two things force the slow path. A change of view — zoom, pan, resize — genuinely
 * needs everything drawn again. And so does a new highlighter stroke on a board that
 * already carries pen ink: paint can only go on top of paint, and SRS C4 says that
 * line has to end up underneath.
 */
export function planBoardPaint(
  painted: BoardMemory,
  key: string,
  counts: readonly number[],
): BoardPaintPlan {
  const from = counts.map((count, band) => Math.min(painted.counts[band] ?? 0, count));
  const startOver =
    painted.key !== key ||
    // A stroke landing in a band that already has paint on top of it: paint can only
    // go over paint, so the layer has to be drawn again from the bottom.
    counts.some(
      (count, band) =>
        count > (from[band] ?? 0) && from.slice(band + 1).some((painted) => painted > 0),
    );
  if (startOver) return { clear: true, from: counts.map(() => 0) };
  return { clear: false, from };
}

/** The board's finished ink, as the painter needs to see it. */
export interface FinishedInk {
  /** One list of strokes per band, in band order. */
  readonly bands: readonly (readonly Stroke[])[];
  /** What the layer already holds. Updated as it is painted. */
  readonly board: BoardMemory;
  readonly byId: Map<string, Stroke>;
  /** The order strokes were drawn in, so a culled set goes back in sequence. */
  readonly order: Map<string, number>;
  readonly index: Quadtree;
}

/** One finished stroke, simplified to what this zoom can actually show. */
function drawOne(ctx: CanvasRenderingContext2D, stroke: Stroke, viewport: Viewport): void {
  drawInk(
    ctx,
    simplifiedPointsOf(stroke, toleranceFor(viewport.zoom)),
    styleOf(stroke),
    viewport,
  );
}

/** The strokes in an area, highlighter first and then in the order they were drawn. */
export function strokesIn(board: FinishedInk, area: Rect): Stroke[] {
  const found: Stroke[] = [];
  for (const { id } of board.index.query(area)) {
    const stroke = board.byId.get(id);
    if (stroke) found.push(stroke);
  }
  return found.sort(
    (a, b) =>
      paintBand(a) - paintBand(b) ||
      (board.order.get(a.id) ?? 0) - (board.order.get(b.id) ?? 0),
  );
}

/**
 * Draws the finished work.
 *
 * Economies from docs/03-ARCHITECTURE.md §7.4, all about a lesson that has been
 * running for three hours: only strokes actually on screen are drawn, those drawn
 * while zoomed out come from a simplified copy, and a new stroke is added to what is
 * already on the layer rather than triggering a redraw of everything.
 */
export function paintBoard(
  board: FinishedInk,
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
): void {
  const key = viewportKeyOf(viewport);
  const counts = board.bands.map((band) => band.length);
  const plan = planBoardPaint(board.board, key, counts);

  if (plan.clear) {
    clearCanvas(ctx);
    for (const stroke of strokesIn(board, visibleCanvasRect(viewport))) {
      drawOne(ctx, stroke, viewport);
    }
  } else {
    board.bands.forEach((band, index) => {
      for (const stroke of band.slice(plan.from[index] ?? 0)) drawOne(ctx, stroke, viewport);
    });
  }

  board.board.counts = [...counts];
  board.board.key = key;
}
